"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { QRCodeSVG } from "qrcode.react";
import { BookText, Banknote, CreditCard, Printer, QrCode, Split } from "lucide-react";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type PaymentRow = {
  payment_mode: "cash" | "upi" | "card" | "credit" | "loyalty";
  amount: number;
  reference_no?: string | null;
};

/** Returned by parent after POST /api/sales — drives success dialog + thermal preview. */
export type SaleFlowMeta = {
  id: number;
  bill_no: string;
  public_invoice_url?: string | null;
  customer: {
    id: number;
    name: string;
    phone: string;
    email?: string | null;
    gstin?: string | null;
    address?: string | null;
  } | null;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  netAmount: number;
  merchantUpi?: string | null;
  /** Customer email from POS (required for Diary / due tab). */
  customerEmail?: string | null;
  onPaid: (rows: PaymentRow[], notes?: string) => Promise<SaleFlowMeta>;
  /** Fires when payment modal finishes (after thermal step closes, or if dialog dismissed early). */
  onFlowFinished?: (meta: SaleFlowMeta) => void;
};

function looksLikeEmail(s: string): boolean {
  const t = s.trim();

  return t.length > 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

export type ThermalReceiptItem = {
  name: string;
  amount: string;
};

export type ThermalReceiptKv = {
  label: string;
  value: string;
};

export type ThermalReceiptUi = {
  company_name: string;
  logo_url: string | null;
  bill_no: string;
  date: string;
  customer: ThermalReceiptKv[];
  items: ThermalReceiptItem[];
  subtotal: string;
  total: string;
  bill_status: string;
  thank_you: string;
  lines: string[];
};

const PADS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"];

async function fetchThermalReceiptUi(saleId: number): Promise<ThermalReceiptUi> {
  const { data } = await api.get<ThermalReceiptUi>(`/api/sales/${saleId}/thermal-ui`);

  return data;
}

export function PaymentModal({
  open,
  onOpenChange,
  netAmount,
  merchantUpi,
  customerEmail,
  onPaid,
  onFlowFinished,
}: Props) {
  const [tab, setTab] = useState("cash");
  const [cashStr, setCashStr] = useState("");
  const [cardRef, setCardRef] = useState("");
  const [splitRows, setSplitRows] = useState<PaymentRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState<"pay" | "thermal">("pay");
  const [thermalContext, setThermalContext] = useState<"cash" | "diary" | "digital">("cash");
  const [thermalReceipt, setThermalReceipt] = useState<ThermalReceiptUi | null>(null);
  const [thermalErr, setThermalErr] = useState<string | null>(null);
  const [pendingMeta, setPendingMeta] = useState<SaleFlowMeta | null>(null);
  const thermalPrintRef = useRef<HTMLDivElement>(null);
  const flowDeliveredRef = useRef(false);

  const printThermal = useReactToPrint({
    contentRef: thermalPrintRef,
    documentTitle: pendingMeta ? `receipt-${pendingMeta.bill_no}` : "receipt",
    pageStyle: `
      @page { size: 80mm auto; margin: 4mm; }
      body { font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; font-size: 12px; color: #000; }
      img { max-height: 96px; object-fit: contain; }
    `,
  });

  useEffect(() => {
    if (!open) {
      flowDeliveredRef.current = false;
      setPhase("pay");
      setThermalContext("cash");
      setThermalReceipt(null);
      setThermalErr(null);
      setPendingMeta(null);
    }
  }, [open]);

  function finishFlow(meta: SaleFlowMeta) {
    if (flowDeliveredRef.current) {
      onOpenChange(false);

      return;
    }
    flowDeliveredRef.current = true;
    onFlowFinished?.(meta);
    onOpenChange(false);
  }

  function handleDialogOpenChange(v: boolean) {
    if (!v && phase === "thermal" && pendingMeta) {
      finishFlow(pendingMeta);
      return;
    }
    onOpenChange(v);
  }

  const cashTender = useMemo(() => parseFloat(cashStr || "0") || 0, [cashStr]);
  const changeDue = useMemo(
    () => Math.max(0, Math.round((cashTender - netAmount) * 100) / 100),
    [cashTender, netAmount],
  );

  const upiUri = useMemo(() => {
    const pa = merchantUpi?.trim() || "merchant@upi";
    const am = netAmount.toFixed(2);

    return `upi://pay?pa=${encodeURIComponent(pa)}&pn=${encodeURIComponent("invoice.cprawat.com")}&am=${am}&cu=INR`;
  }, [merchantUpi, netAmount]);

  const diaryEmailOk = useMemo(() => looksLikeEmail(customerEmail ?? ""), [customerEmail]);

  function appendCash(d: string) {
    if (d === "C") {
      setCashStr("");

      return;
    }
    if (d === "⌫") {
      setCashStr((s) => s.slice(0, -1));

      return;
    }
    if (d === "." && cashStr.includes(".")) {
      return;
    }
    if (cashStr === "0" && d !== ".") {
      setCashStr(d);

      return;
    }
    setCashStr((s) => (s + d).slice(0, 12));
  }

  async function loadThermalAfterSave(meta: SaleFlowMeta) {
    setPendingMeta(meta);
    try {
      const ui = await fetchThermalReceiptUi(meta.id);
      setThermalReceipt(ui);
    } catch (fe) {
      const feMsg = fe instanceof Error ? fe.message : "Could not load receipt";
      setThermalErr(feMsg);
      setThermalReceipt({
        company_name: "",
        logo_url: null,
        bill_no: meta.bill_no,
        date: "",
        customer: [],
        items: [],
        subtotal: "",
        total: "",
        bill_status: thermalContext === "diary" ? "Status: Due" : "Status: Paid",
        thank_you: "Thank You",
        lines: ["—"],
      });
    }
    setPhase("thermal");
  }

  async function payCashFull() {
    if (cashTender + 1e-6 < netAmount) {
      return;
    }
    setSubmitting(true);
    setThermalErr(null);
    setThermalContext("cash");
    try {
      const meta = await onPaid([{ payment_mode: "cash", amount: netAmount }]);
      await loadThermalAfterSave(meta);
    } catch (e) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { error?: string } } }).response?.data?.error)
          : e instanceof Error
            ? e.message
            : "Sale failed";
      window.alert(msg || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function payUpiFull() {
    setSubmitting(true);
    setThermalErr(null);
    setThermalContext("digital");
    try {
      const meta = await onPaid([{ payment_mode: "upi", amount: netAmount }]);
      await loadThermalAfterSave(meta);
    } catch (e) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { error?: string } } }).response?.data?.error)
          : e instanceof Error
            ? e.message
            : "Sale failed";
      window.alert(msg || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function payCardFull() {
    setSubmitting(true);
    setThermalErr(null);
    setThermalContext("digital");
    try {
      const meta = await onPaid([
        {
          payment_mode: "card",
          amount: netAmount,
          reference_no: cardRef.trim() || null,
        },
      ]);
      await loadThermalAfterSave(meta);
    } catch (e) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { error?: string } } }).response?.data?.error)
          : e instanceof Error
            ? e.message
            : "Sale failed";
      window.alert(msg || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function paySplit() {
    const sum = splitRows.reduce((a, r) => a + r.amount, 0);
    if (Math.abs(sum - netAmount) > 0.05) {
      return;
    }
    setSubmitting(true);
    setThermalErr(null);
    setThermalContext("digital");
    try {
      const meta = await onPaid(splitRows);
      await loadThermalAfterSave(meta);
    } catch (e) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { error?: string } } }).response?.data?.error)
          : e instanceof Error
            ? e.message
            : "Sale failed";
      window.alert(msg || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function payDiary() {
    if (!diaryEmailOk) {
      return;
    }
    setSubmitting(true);
    setThermalErr(null);
    setThermalContext("diary");
    try {
      const meta = await onPaid(
        [{ payment_mode: "credit", amount: netAmount }],
        "Diary / due account (monthly settlement)",
      );
      await loadThermalAfterSave(meta);
    } catch (e) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { error?: string } } }).response?.data?.error)
          : e instanceof Error
            ? e.message
            : "Sale failed";
      window.alert(msg || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className={
          phase === "thermal"
            ? "flex max-h-[92vh] w-[min(100vw-1rem,56rem)] max-w-[min(100vw-1rem,56rem)] flex-col gap-0 overflow-hidden p-0 sm:p-6"
            : "max-h-[90vh] overflow-y-auto sm:max-w-lg"
        }
      >
        {phase === "thermal" && pendingMeta ? (
          <>
            <DialogHeader className="border-b px-4 pb-3 pt-4 sm:px-0 sm:pt-0">
              <DialogTitle className="text-base">Receipt · {pendingMeta.bill_no}</DialogTitle>
            </DialogHeader>
            <div className="grid min-h-0 flex-1 gap-3 overflow-hidden px-4 pb-4 sm:grid-cols-2 sm:px-0 sm:pb-0">
              <div className="flex flex-col gap-3 border-b pb-3 sm:border-b-0 sm:border-r sm:pr-3 sm:pb-0">
                {thermalContext === "diary" ? (
                  <>
                    <p className="text-sm font-semibold text-amber-600">Saved to diary (due later).</p>
                    <p className="text-sm text-muted-foreground">
                      Outstanding added:{" "}
                      <span className="font-mono font-semibold text-foreground">
                        ₹{netAmount.toLocaleString("en-IN")}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Invoice + UPI QR emailed to customer (if SMTP configured).
                    </p>
                  </>
                ) : thermalContext === "cash" ? (
                  <>
                    <p className="text-sm text-emerald-600">Cash sale saved.</p>
                    <p className="text-sm text-muted-foreground">
                      Change due to customer:{" "}
                      <span className="font-mono font-semibold text-foreground">
                        ₹{changeDue.toLocaleString("en-IN")}
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-emerald-600">Sale saved.</p>
                    <p className="text-sm text-muted-foreground">
                      Use Print thermal below, then Done when finished.
                    </p>
                  </>
                )}
                <div className="mt-auto flex flex-col gap-2 sm:mt-4">
                  <Button type="button" variant="secondary" onClick={() => void printThermal()}>
                    <Printer className="mr-2 h-4 w-4" />
                    Print thermal
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      if (pendingMeta) {
                        finishFlow(pendingMeta);
                      }
                    }}
                  >
                    Done
                  </Button>
                </div>
              </div>
              <div className="flex min-h-[200px] flex-col gap-2 sm:min-h-0">
                <p className="text-xs font-medium text-muted-foreground">Preview (same panel)</p>
                {thermalErr ? (
                  <p className="text-sm text-destructive">{thermalErr}</p>
                ) : null}
                <ScrollArea className="h-[220px] rounded-md border bg-muted/30 sm:h-[min(55vh,420px)]">
                  <div ref={thermalPrintRef} className="bg-white text-black print:bg-white">
                    <ThermalReceiptView receipt={thermalReceipt} />
                  </div>
                </ScrollArea>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Amount due</p>
                <p className="text-3xl font-bold tabular-nums">₹{netAmount.toLocaleString("en-IN")}</p>
              </div>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList
                  variant="line"
                  className="grid h-auto w-full grid-cols-3 gap-2 bg-transparent p-0 sm:grid-cols-5"
                >
                  {[
                    { id: "cash", label: "Cash", Icon: Banknote },
                    { id: "upi", label: "UPI", Icon: QrCode },
                    { id: "card", label: "Card", Icon: CreditCard },
                    { id: "split", label: "Split", Icon: Split },
                    { id: "diary", label: "Diary due", Icon: BookText },
                  ].map(({ id, label, Icon }) => (
                    <TabsTrigger
                      key={id}
                      value={id}
                      className="flex h-20 flex-col items-center justify-center gap-1 rounded-lg border border-border bg-card p-2 text-xs font-semibold text-foreground/70 shadow-sm transition-colors hover:border-primary/40 hover:bg-accent hover:text-foreground data-active:border-primary data-active:bg-primary data-active:text-primary-foreground data-active:shadow-md"
                    >
                      <Icon className="h-5 w-5" />
                      <span>{label}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="cash" className="space-y-3 pt-2">
                  <div>
                    <Label>Cash tendered</Label>
                    <Input readOnly className="mt-1 font-mono text-lg" value={cashStr} placeholder="0" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {PADS.map((d) => (
                      <Button
                        key={d}
                        type="button"
                        variant={d === "C" ? "destructive" : "secondary"}
                        onClick={() => appendCash(d)}
                      >
                        {d}
                      </Button>
                    ))}
                  </div>
                  <Button type="button" variant="outline" className="w-full" onClick={() => appendCash(".")}>
                    Decimal
                  </Button>
                  {cashTender >= netAmount - 1e-6 ? (
                    <p className="text-center text-sm text-emerald-600">
                      Return ₹{changeDue.toLocaleString("en-IN")}
                    </p>
                  ) : (
                    <p className="text-center text-sm text-amber-600">Tender less than due — use Split</p>
                  )}
                  <Button
                    className="w-full"
                    size="lg"
                    disabled={cashTender + 1e-6 < netAmount || submitting}
                    onClick={() => void payCashFull()}
                  >
                    Charge cash
                  </Button>
                </TabsContent>

                <TabsContent value="upi" className="flex flex-col items-center gap-3 pt-2">
                  <QRCodeSVG value={upiUri} size={200} level="M" />
                  <p className="text-center text-xs text-muted-foreground break-all">{upiUri}</p>
                  <Button className="w-full" size="lg" disabled={submitting} onClick={() => void payUpiFull()}>
                    Mark UPI paid
                  </Button>
                </TabsContent>

                <TabsContent value="card" className="space-y-3 pt-2">
                  <div>
                    <Label>Reference (optional)</Label>
                    <Input value={cardRef} onChange={(e) => setCardRef(e.target.value)} className="mt-1" />
                  </div>
                  <Button className="w-full" size="lg" disabled={submitting} onClick={() => void payCardFull()}>
                    Charge card
                  </Button>
                </TabsContent>

                <TabsContent value="split" className="space-y-3 pt-2">
                  {splitRows.map((row, i) => (
                    <div key={i} className="flex gap-2">
                      <select
                        className="rounded-md border border-input bg-background px-2 text-sm"
                        value={row.payment_mode}
                        onChange={(e) => {
                          const v = e.target.value as PaymentRow["payment_mode"];
                          setSplitRows((rows) =>
                            rows.map((r, j) => (j === i ? { ...r, payment_mode: v } : r)),
                          );
                        }}
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="card">Card</option>
                        <option value="credit">Credit</option>
                        <option value="loyalty">Loyalty</option>
                      </select>
                      <Input
                        type="number"
                        className="font-mono"
                        value={row.amount || ""}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setSplitRows((rows) => rows.map((r, j) => (j === i ? { ...r, amount: v } : r)));
                        }}
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setSplitRows((r) => r.filter((_, j) => j !== i))}>
                        ✕
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      setSplitRows((r) => [...r, { payment_mode: "cash", amount: 0 }])
                    }
                  >
                    Add row
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Sum: ₹
                    {splitRows.reduce((a, x) => a + x.amount, 0).toFixed(2)} / ₹{netAmount.toFixed(2)}
                  </p>
                  <Button
                    className="w-full"
                    size="lg"
                    disabled={
                      submitting ||
                      Math.abs(splitRows.reduce((a, x) => a + x.amount, 0) - netAmount) > 0.05 ||
                      splitRows.length === 0
                    }
                    onClick={() => void paySplit()}
                  >
                    Submit split
                  </Button>
                </TabsContent>

                <TabsContent value="diary" className="space-y-3 pt-2">
                  <p className="text-sm text-muted-foreground">
                    Save full bill as <strong className="text-foreground">credit / due</strong> for monthly payers. Customer
                    outstanding balance increases by this amount.
                  </p>
                  {!diaryEmailOk ? (
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      Enter a valid customer email on POS (left panel) so invoice + UPI QR can be emailed.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Email on file: <span className="font-mono text-foreground">{customerEmail?.trim()}</span>
                    </p>
                  )}
                  <Button
                    className="w-full"
                    size="lg"
                    disabled={submitting || !diaryEmailOk}
                    onClick={() => void payDiary()}
                  >
                    Save as diary (due later)
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ThermalReceiptView({ receipt }: { receipt: ThermalReceiptUi | null }) {
  if (!receipt) {
    return null;
  }
  const hasStruct = receipt.items.length > 0 || receipt.total !== "" || receipt.company_name !== "";
  if (!hasStruct) {
    return (
      <pre className="m-0 whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-snug">
        {receipt.lines.join("\n")}
      </pre>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[260px] px-2 py-3 font-mono text-[10px] text-black print:max-w-[320px] print:px-3 print:py-4 print:text-[13px]">
      <div className="text-center text-[14px] font-bold uppercase leading-tight tracking-wide print:text-[20px]">
        {receipt.company_name}
      </div>
      <div className="mt-0.5 text-center text-[12px] font-bold uppercase tracking-wide print:text-[16px]">
        Receipt
      </div>
      {receipt.date ? (
        <div className="mt-2 text-center text-[11px] print:mt-3 print:text-[14px]">
          {receipt.date}
        </div>
      ) : null}

      {receipt.customer.length > 0 ? (
        <div className="mt-2 space-y-0.5 text-[10px] print:mt-3 print:text-[13px]">
          {receipt.customer.map((row, i) => {
            const oneLine = row.label === "Mob." || row.label === "GSTIN";

            return (
              <div key={i} className="flex gap-1 break-words">
                <span className="font-semibold whitespace-nowrap">{row.label}:</span>
                <span className={oneLine ? "whitespace-nowrap" : "break-all"}>{row.value}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      <table className="mt-3 w-full border-collapse print:mt-4">
        <tbody>
          {receipt.items.map((it, i) => (
            <tr key={i}>
              <td className="py-[2px] align-top text-[11px] print:py-[3px] print:text-[14px]">
                {it.name}
              </td>
              <td className="py-[2px] pl-2 text-right align-top text-[11px] whitespace-nowrap print:py-[3px] print:text-[14px]">
                ₹{it.amount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {receipt.subtotal ? (
        <div className="mt-2 text-[11px] print:mt-3 print:text-[14px]">Total ₹{receipt.subtotal}</div>
      ) : null}
      <div className="my-1.5 border-t border-dashed border-black print:my-2" />
      {receipt.total ? (
        <div className="flex items-baseline justify-between text-[14px] font-bold uppercase print:text-[20px]">
          <span>Total</span>
          <span>₹{receipt.total}</span>
        </div>
      ) : null}
      {receipt.bill_no ? (
        <div className="mt-1 text-right text-[10px] tracking-wide print:text-[13px]">
          {receipt.bill_no}
        </div>
      ) : null}

      {receipt.bill_status ? (
        <div className="mt-2 text-center text-[11px] font-semibold print:mt-3 print:text-[14px]">
          {receipt.bill_status}
        </div>
      ) : null}

      <div className="mt-3 text-center text-[12px] font-bold print:mt-5 print:text-[16px]">
        {receipt.thank_you || "Thank You"}
      </div>
      {receipt.logo_url ? (
        <div className="mt-3 text-right print:mt-4">
          <img
            src={receipt.logo_url}
            alt=""
            className="ml-auto block h-10 max-w-[56px] object-contain print:h-16 print:max-w-[80px]"
          />
        </div>
      ) : null}
    </div>
  );
}

/** Open PDF in new tab using authenticated blob fetch. */
export async function openSalePdf(saleId: number) {
  const res = await api.get(`/api/sales/${saleId}/pdf`, { responseType: "blob" });
  const ct = String(res.headers?.["content-type"] ?? "");
  // Backend may return JSON error as blob (401/403/etc). Avoid opening useless tab.
  if (!ct.toLowerCase().includes("application/pdf")) {
    const text = await res.data.text().catch(() => "");
    throw new Error(text || "PDF generation failed");
  }
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Trigger browser download (same PDF as print view). */
export async function downloadSalePdf(saleId: number, billNo: string) {
  const res = await api.get(`/api/sales/${saleId}/pdf`, { responseType: "blob" });
  const ct = String(res.headers?.["content-type"] ?? "");
  if (!ct.toLowerCase().includes("application/pdf")) {
    const text = await res.data.text().catch(() => "");
    throw new Error(text || "PDF generation failed");
  }
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${billNo.replace(/[^\w.-]+/g, "_")}.pdf`;
  a.rel = "noopener";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function openSaleThermal(saleId: number) {
  const { data } = await api.get<ThermalReceiptUi>(`/api/sales/${saleId}/thermal-ui`);
  const w = window.open("", "_blank", "noopener,noreferrer");
  if (!w) {
    return;
  }
  w.document.write(thermalReceiptHtml(data));
  w.document.close();
}

export function thermalReceiptHtml(r: ThermalReceiptUi): string {
  const css = `
    *{box-sizing:border-box}
    html,body{margin:0;padding:0;background:#fff;color:#000}
    body{font-family:ui-monospace,"SFMono-Regular",Menlo,monospace;font-size:13px;padding:14px}
    .wrap{max-width:320px;margin:0 auto}
    .name{text-align:center;font-weight:700;font-size:22px;line-height:1.15;letter-spacing:.5px;text-transform:uppercase}
    .title{text-align:center;font-weight:700;font-size:18px;letter-spacing:.5px;text-transform:uppercase;margin-top:2px}
    .date{text-align:center;margin-top:14px;font-size:14px}
    .cust{margin-top:12px;font-size:13px;line-height:1.35}
    .cust .row{display:flex;gap:6px;word-break:break-word}
    .cust .row.nowrap{white-space:nowrap;word-break:keep-all}
    .cust .lbl{font-weight:700;white-space:nowrap}
    .items{margin-top:18px;width:100%;border-collapse:collapse}
    .items td{padding:3px 0;font-size:14px;vertical-align:top}
    .items td.amt{text-align:right;white-space:nowrap;padding-left:8px}
    .sub{margin-top:14px;font-size:14px}
    .rule{border-top:1px dashed #000;margin:8px 0}
    .total{display:flex;justify-content:space-between;font-size:22px;font-weight:700;text-transform:uppercase}
    .ref{text-align:right;font-size:13px;margin-top:4px;letter-spacing:.5px}
    .billstat{text-align:center;font-weight:600;font-size:15px;margin-top:14px}
    .thanks{text-align:center;font-weight:700;font-size:18px;margin-top:22px}
    .logo{margin-top:18px;text-align:right}
    .logo img{max-height:64px;max-width:80px;object-fit:contain}
  `;
  const items = r.items
    .map(
      (it) =>
        `<tr><td>${escapeHtml(it.name)}</td><td class="amt">₹${escapeHtml(it.amount)}</td></tr>`,
    )
    .join("");
  const logo = r.logo_url
    ? `<div class="logo"><img src="${escapeHtml(r.logo_url)}" alt=""></div>`
    : "";
  const ref = r.bill_no ? `<div class="ref">${escapeHtml(r.bill_no)}</div>` : "";
  const stat = r.bill_status ? `<div class="billstat">${escapeHtml(r.bill_status)}</div>` : "";
  const date = r.date ? `<div class="date">${escapeHtml(r.date)}</div>` : "";
  const cust =
    r.customer.length > 0
      ? `<div class="cust">${r.customer
          .map((row) => {
            const cls = row.label === "Mob." || row.label === "GSTIN" ? "row nowrap" : "row";

            return `<div class="${cls}"><span class="lbl">${escapeHtml(row.label)}:</span><span>${escapeHtml(row.value)}</span></div>`;
          })
          .join("")}</div>`
      : "";

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt ${escapeHtml(r.bill_no)}</title><style>${css}</style></head><body><div class="wrap"><div class="name">${escapeHtml(r.company_name)}</div><div class="title">Receipt</div>${date}${cust}<table class="items">${items}</table><div class="sub">Total ₹${escapeHtml(r.subtotal)}</div><div class="rule"></div><div class="total"><span>Total</span><span>₹${escapeHtml(r.total)}</span></div>${ref}${stat}<div class="thanks">${escapeHtml(r.thank_you || "Thank You")}</div>${logo}</div></body></html>`;
}

function escapeHtml(s: string) {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function buildWhatsAppLink(phoneDigits: string, billText: string) {
  const n = phoneDigits.replace(/\D/g, "");
  const body = encodeURIComponent(billText);

  return `https://wa.me/91${n}?text=${body}`;
}
